import { useRef, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Download, Upload, Trash2, GripVertical, Eye, FileText, Save, ChevronDown, X, Pencil } from 'lucide-react';
import { meetingsApi, usersApi } from '../../services/api';
import Modal from '../shared/Modal';
import { formatDate, MEETING_TYPE_LABELS } from '../../utils/formatters';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const AGENDA_CATEGORIES = [
  'Chamados e Desobrigações',
  'Entrevistas',
  'Outros Assuntos',
];

const PRESIDENCY_ROLES = ['Presidente da Estaca', '1º Conselheiro', '2º Conselheiro'];

export default function MeetingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAddItem, setShowAddItem] = useState(false);
  const [category, setCategory] = useState('Outros Assuntos');
  const [content, setContent] = useState('');
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [editCategory, setEditCategory] = useState('Outros Assuntos');
  const [editContent, setEditContent] = useState('');
  const [showAgendaPreview, setShowAgendaPreview] = useState(false);
  const [showMinutesViewer, setShowMinutesViewer] = useState(false);
  const [showMeta, setShowMeta] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [newDate, setNewDate] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Meeting metadata state
  const [meta, setMeta] = useState({
    presidida_por: '',
    dirigida_por: '',
    oracao_abertura: '',
    pensamento: '',
    oracao_encerramento: '',
  });

  const { data: meetingData, isLoading: meetingLoading } = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => meetingsApi.get(id!),
    enabled: !!id,
  });

  const { data: agendaData, isLoading: agendaLoading } = useQuery({
    queryKey: ['agenda', id],
    queryFn: () => meetingsApi.getAgenda(id!),
    enabled: !!id,
  });

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    staleTime: 60_000,
  });

  const meeting = meetingData?.data as any;
  const items = (agendaData?.data || []) as any[];
  const allUsers = usersData?.data || [];
  const presidencyMembers = allUsers.filter((u: any) => PRESIDENCY_ROLES.includes(u.role_label));

  // Sync metadata from meeting data
  useEffect(() => {
    if (meeting) {
      setMeta({
        presidida_por: meeting.presidida_por || '',
        dirigida_por: meeting.dirigida_por || '',
        oracao_abertura: meeting.oracao_abertura || '',
        pensamento: meeting.pensamento || '',
        oracao_encerramento: meeting.oracao_encerramento || '',
      });
    }
  }, [meeting?.id]);

  const addItemMutation = useMutation({
    mutationFn: () => meetingsApi.addAgendaItem(id!, { category, content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agenda', id] });
      queryClient.invalidateQueries({ queryKey: ['meeting', id] });
      toast.success('Item adicionado!');
      setShowAddItem(false);
      setContent('');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) => meetingsApi.deleteAgendaItem(id!, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agenda', id] });
      queryClient.invalidateQueries({ queryKey: ['meeting', id] });
      toast.success('Item removido');
    },
  });

  const updateItemMutation = useMutation({
    mutationFn: () => {
      if (!editingItem) throw new Error('Item invalido');
      return meetingsApi.updateAgendaItem(id!, editingItem.id, {
        category: editCategory,
        content: editContent,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agenda', id] });
      queryClient.invalidateQueries({ queryKey: ['meeting', id] });
      toast.success('Item atualizado');
      setEditingItem(null);
      setEditContent('');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const uploadMinutesMutation = useMutation({
    mutationFn: (file: File) => meetingsApi.uploadMinutes(id!, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meeting', id] });
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      toast.success('Ata enviada com sucesso!');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const deleteMeetingMutation = useMutation({
    mutationFn: () => meetingsApi.delete(id!),
    onSuccess: () => {
      navigate('/reunioes');
      toast.success('Reunião excluída');
    },
  });

  const updateDateMutation = useMutation({
    mutationFn: (date: string) => {
      const today = new Date().toISOString().split('T')[0];
      const newStatus = date >= today ? 'upcoming' : 'completed';
      return meetingsApi.update(id!, { date, status: newStatus });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meeting', id] });
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      toast.success('Data atualizada!');
      setEditingDate(false);
    },
    onError: (err: any) => toast.error(err.message),
  });

  const saveMetaMutation = useMutation({
    mutationFn: () => meetingsApi.update(id!, meta),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meeting', id] });
      toast.success('Informações salvas!');
    },
    onError: (err: any) => toast.error(err.message),
  });

  if (meetingLoading) {
    return (
      <div className="space-y-4">
        <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
        <div className="card animate-pulse h-24" />
        <div className="card animate-pulse h-40" />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Reunião não encontrada</p>
        <button onClick={() => navigate('/reunioes')} className="btn-primary mt-4">
          Voltar
        </button>
      </div>
    );
  }

  const isUpcoming = meeting.status === 'upcoming';

  // Group items by category
  const grouped = items.reduce<Record<string, any[]>>((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  const getPresidencyName = (id: string) =>
    presidencyMembers.find((u: any) => u.id === id)?.name || id;

  const openEditItem = (item: any) => {
    setEditingItem(item);
    setEditCategory(item.category);
    setEditContent(item.content);
  };

  return (
    <div>
      {/* Back button */}
      <button
        onClick={() => navigate('/reunioes')}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
      >
        <ArrowLeft size={16} /> Reuniões
      </button>

      {/* Meeting header */}
      <div className="card mb-4">
        <h2 className="font-semibold text-gray-800 text-lg">
          {MEETING_TYPE_LABELS[meeting.type] || meeting.type}
        </h2>
        <div className="flex items-center gap-2 mt-1">
          {editingDate ? (
            <>
              <input
                type="date"
                value={newDate}
                onChange={e => setNewDate(e.target.value)}
                className="input text-sm py-1 w-auto"
                autoFocus
              />
              <button
                onClick={() => newDate && updateDateMutation.mutate(newDate)}
                disabled={!newDate || updateDateMutation.isPending}
                className="text-brand-500 hover:text-brand-600 disabled:opacity-40"
                aria-label="Salvar data"
              >
                <Save size={14} />
              </button>
              <button
                onClick={() => setEditingDate(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Cancelar"
              >
                <X size={14} />
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500">
                {formatDate(meeting.date)}{meeting.time ? ` às ${meeting.time}` : ''}
              </p>
              <button
                onClick={() => { setNewDate(meeting.date); setEditingDate(true); }}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Editar data"
              >
                <Pencil size={12} />
              </button>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className={clsx(
            'text-xs font-medium px-2 py-0.5 rounded-full',
            meeting.status === 'upcoming'
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-600'
          )}>
            {meeting.status === 'upcoming' ? 'Próxima' : 'Realizada'}
          </span>
          {meeting.minutes_file_url && (
            <span className="text-xs text-green-600">✓ Ata disponível</span>
          )}
        </div>
      </div>

      {/* Meeting metadata fields */}
      <div className="card mb-4">
        <button
          type="button"
          className="w-full flex items-center justify-between"
          onClick={() => setShowMeta(s => !s)}
        >
          <h3 className="font-semibold text-gray-800 text-sm">Informações da Reunião</h3>
          <ChevronDown
            size={16}
            className={clsx('text-gray-400 transition-transform duration-200', showMeta && 'rotate-180')}
          />
        </button>
        {showMeta && (
          <div className="space-y-3 mt-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Presidida por</label>
                <select
                  value={meta.presidida_por}
                  onChange={e => setMeta(m => ({ ...m, presidida_por: e.target.value }))}
                  className="input"
                  disabled={!isUpcoming}
                >
                  <option value="">— Selecionar —</option>
                  {presidencyMembers.map((u: any) => (
                    <option key={u.id} value={u.id}>{u.name.split(' ').slice(0, 2).join(' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Dirigida por</label>
                <select
                  value={meta.dirigida_por}
                  onChange={e => setMeta(m => ({ ...m, dirigida_por: e.target.value }))}
                  className="input"
                  disabled={!isUpcoming}
                >
                  <option value="">— Selecionar —</option>
                  {presidencyMembers.map((u: any) => (
                    <option key={u.id} value={u.id}>{u.name.split(' ').slice(0, 2).join(' ')}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="label">Pensamento</label>
              <input
                type="text"
                value={meta.pensamento}
                onChange={e => setMeta(m => ({ ...m, pensamento: e.target.value }))}
                className="input"
                placeholder="Nome..."
                disabled={!isUpcoming}
              />
            </div>
            <div>
              <label className="label">Oração de abertura</label>
              <input
                type="text"
                value={meta.oracao_abertura}
                onChange={e => setMeta(m => ({ ...m, oracao_abertura: e.target.value }))}
                className="input"
                placeholder="Nome..."
                disabled={!isUpcoming}
              />
            </div>
            <div>
              <label className="label">Oração de encerramento</label>
              <input
                type="text"
                value={meta.oracao_encerramento}
                onChange={e => setMeta(m => ({ ...m, oracao_encerramento: e.target.value }))}
                className="input"
                placeholder="Nome..."
                disabled={!isUpcoming}
              />
            </div>
            {isUpcoming && (
              <button
                onClick={() => saveMetaMutation.mutate()}
                disabled={saveMetaMutation.isPending}
                className="flex items-center gap-1.5 text-sm font-medium text-brand-500 hover:text-brand-600"
              >
                <Save size={14} />
                {saveMetaMutation.isPending ? 'Salvando...' : 'Salvar informações'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <a
          href={meetingsApi.downloadAgendaPdf(id!)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-sm font-medium text-gray-600 border border-gray-200 bg-white px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Download size={14} /> Criar Agenda
        </a>
        <button
          onClick={() => setShowAgendaPreview(true)}
          className="flex items-center gap-1.5 text-sm font-medium text-gray-600 border border-gray-200 bg-white px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Eye size={14} /> Visualizar Agenda
        </button>
        <a
          href={meetingsApi.downloadAgendaDocx(id!)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-sm font-medium text-gray-600 border border-gray-200 bg-white px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Download size={14} /> Baixar .docx
        </a>
        <label className="flex items-center gap-1.5 text-sm font-medium text-gray-600 border border-gray-200 bg-white px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
          <Upload size={14} />
          {uploadMinutesMutation.isPending ? 'Enviando...' : 'Enviar Ata'}
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx,.doc,.rtf,.pdf,.txt"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadMinutesMutation.mutate(f);
            }}
          />
        </label>
        {meeting.minutes_file_url && (
          <button
            onClick={() => setShowMinutesViewer(true)}
            className="flex items-center gap-1.5 text-sm text-brand-500 hover:underline"
          >
            <Eye size={14} /> Ver Ata
          </button>
        )}
      </div>

      {/* Agenda */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800">
            Pauta ({items.length} {items.length === 1 ? 'item' : 'itens'})
          </h3>
          {isUpcoming && (
            <button
              onClick={() => setShowAddItem(true)}
              className="flex items-center gap-1.5 text-sm font-medium text-brand-500 hover:text-brand-600"
            >
              <Plus size={16} /> Adicionar
            </button>
          )}
        </div>

        {agendaLoading ? (
          <div className="space-y-2">
            {[1, 2].map(i => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">
            {isUpcoming ? 'Pauta vazia. Adicione itens!' : 'Nenhum item registrado.'}
          </p>
        ) : (
          <div className="space-y-4">
            {Object.entries(grouped).map(([cat, catItems]) => (
              <div key={cat}>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                  {cat}
                </p>
                <div className="space-y-1">
                  {catItems.map((item) => (
                    <div key={item.id} className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-50 group">
                      <GripVertical size={16} className="text-gray-300 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700">{item.content}</p>
                        {item.added_by_name && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            Adicionado por {item.added_by_name.split(' ')[0]}
                          </p>
                        )}
                      </div>
                      {isUpcoming && (
                        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => openEditItem(item)}
                            className="text-gray-300 hover:text-brand-500 transition-colors p-1"
                            aria-label="Editar item"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => deleteItemMutation.mutate(item.id)}
                            disabled={deleteItemMutation.isPending}
                            className="text-gray-300 hover:text-red-400 transition-colors p-1"
                            aria-label="Remover item"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete meeting */}
      {isUpcoming && (
        <button
          onClick={() => {
            if (window.confirm('Excluir esta reunião?')) {
              deleteMeetingMutation.mutate();
            }
          }}
          disabled={deleteMeetingMutation.isPending}
          className="w-full mt-4 text-sm text-red-400 hover:text-red-600 transition-colors py-2"
        >
          Excluir reunião
        </button>
      )}

      {/* Add agenda item modal */}
      <Modal
        isOpen={showAddItem}
        onClose={() => { setShowAddItem(false); setContent(''); }}
        title="Adicionar item à pauta"
        footer={
          <div className="flex gap-3">
            <button type="button" onClick={() => setShowAddItem(false)} className="btn-ghost flex-1">
              Cancelar
            </button>
            <button
              type="submit"
              form="add-item-form"
              disabled={!content.trim() || addItemMutation.isPending}
              className="btn-primary flex-1"
            >
              {addItemMutation.isPending ? 'Adicionando...' : 'Adicionar'}
            </button>
          </div>
        }
      >
        <form
          id="add-item-form"
          onSubmit={(e) => { e.preventDefault(); addItemMutation.mutate(); }}
          className="space-y-4"
        >
          <div>
            <label className="label">Categoria</label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="input"
            >
              {AGENDA_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="label">
              Conteúdo <span className="text-red-500">*</span>
            </label>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              className="input resize-none"
              rows={3}
              placeholder="Descreva o item da pauta..."
              required
              autoFocus
            />
          </div>
        </form>
      </Modal>

      {/* Visualizar Agenda — tela cheia */}

      {/* Edit agenda item modal */}
      <Modal
        isOpen={!!editingItem}
        onClose={() => {
          setEditingItem(null);
          setEditContent('');
        }}
        title="Editar item da pauta"
        footer={
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                setEditingItem(null);
                setEditContent('');
              }}
              className="btn-ghost flex-1"
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="edit-item-form"
              disabled={!editContent.trim() || updateItemMutation.isPending}
              className="btn-primary flex-1"
            >
              {updateItemMutation.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        }
      >
        <form
          id="edit-item-form"
          onSubmit={(e) => {
            e.preventDefault();
            updateItemMutation.mutate();
          }}
          className="space-y-4"
        >
          <div>
            <label className="label">Categoria</label>
            <select
              value={editCategory}
              onChange={e => setEditCategory(e.target.value)}
              className="input"
            >
              {AGENDA_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Conteudo</label>
            <textarea
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              className="input resize-none"
              rows={3}
              required
            />
          </div>
        </form>
      </Modal>

      {showAgendaPreview && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 pt-10 pb-4 border-b border-gray-100 flex-shrink-0 bg-white sticky top-0 z-10">
            <h3 className="font-semibold text-gray-800">Visualizar Agenda</h3>
            <button
              onClick={() => setShowAgendaPreview(false)}
              className="p-2.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
              aria-label="Fechar"
            >
              <X size={20} />
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-5 text-sm">
            {/* Header */}
            <div className="border-b-2 border-brand-500 pb-3 mb-4">
              <h2 className="font-bold text-brand-700 text-base">Presidência da Estaca Sorocaba Brasil Barcelona</h2>
              <p className="text-gray-700 font-medium mt-0.5">
                {MEETING_TYPE_LABELS[meeting.type]} — {formatDate(meeting.date)}
                {meeting.time ? ` às ${meeting.time}` : ''}
              </p>
            </div>

            {/* Meta fields */}
            {(meta.presidida_por || meta.dirigida_por || meta.oracao_abertura || meta.pensamento || meta.oracao_encerramento) && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4 space-y-1.5 text-xs">
                {meta.presidida_por && (
                  <p><span className="font-semibold text-gray-600">Presidida por:</span> {getPresidencyName(meta.presidida_por)}</p>
                )}
                {meta.dirigida_por && (
                  <p><span className="font-semibold text-gray-600">Dirigida por:</span> {getPresidencyName(meta.dirigida_por)}</p>
                )}
                {meta.pensamento && (
                  <p><span className="font-semibold text-gray-600">Pensamento:</span> {meta.pensamento}</p>
                )}
                {meta.oracao_abertura && (
                  <p><span className="font-semibold text-gray-600">Oração de abertura:</span> {meta.oracao_abertura}</p>
                )}
                {meta.oracao_encerramento && (
                  <p><span className="font-semibold text-gray-600">Oração de encerramento:</span> {meta.oracao_encerramento}</p>
                )}
              </div>
            )}

            {/* Agenda items */}
            {items.length === 0 ? (
              <p className="text-gray-400 text-center py-4">Nenhum item na pauta</p>
            ) : (
              Object.entries(grouped).map(([cat, catItems]) => (
                <div key={cat} className="mb-4">
                  <p className="text-xs font-bold text-brand-600 uppercase tracking-wider mb-2">{cat}</p>
                  <ul className="space-y-1 pl-2">
                    {catItems.map(item => (
                      <li key={item.id} className="text-gray-700 flex items-start gap-2">
                        <span className="text-gray-400 flex-shrink-0">•</span>
                        <span>{item.content}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Ver Ata viewer modal */}
      {showMinutesViewer && meeting.minutes_file_url && (
        <div className="fixed inset-0 z-50 bg-black/70 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 bg-white border-b">
            <h3 className="font-semibold text-gray-800">Ata da Reunião</h3>
            <div className="flex items-center gap-3">
              <a
                href={meeting.minutes_file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-brand-500 hover:underline"
              >
                <Download size={14} /> Baixar
              </a>
              <button
                onClick={() => setShowMinutesViewer(false)}
                className="text-gray-500 hover:text-gray-700 font-medium text-sm"
              >
                Fechar
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            <iframe
              src={meeting.minutes_file_url}
              className="w-full h-full border-0"
              title="Ata da Reunião"
            />
          </div>
        </div>
      )}
    </div>
  );
}
